import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { OutboundParcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import type { DisplayParcel } from "../../utils/use-group-info";

interface ParcelItemBreakdownProps {
  parcelIndex: number;
  selectedParcels: DisplayParcel[];
  items: FulfillmentOrderLineItem[];
  onParcelChange: (parcel: Partial<OutboundParcel>, index: number) => void;
  disabled?: boolean;
}

function getAllocatedQuantity(
  parcels: OutboundParcel[],
  externalId: string,
  excludeIndex?: number,
): number {
  let total = 0;
  for (const [i, parcel] of parcels.entries()) {
    if (i === excludeIndex) {
      continue;
    }
    for (const alloc of parcel.itemAllocations ?? []) {
      if (alloc.externalId === externalId) {
        total += alloc.quantity;
      }
    }
  }
  return total;
}

function getQuantityInParcel(
  parcel: OutboundParcel,
  externalId: string,
): number {
  return (
    parcel.itemAllocations?.find((a) => a.externalId === externalId)
      ?.quantity ?? 0
  );
}

export const ParcelItemBreakdown = memo(function ParcelItemBreakdown({
  parcelIndex,
  selectedParcels,
  items,
  onParcelChange,
  disabled,
}: ParcelItemBreakdownProps) {
  const storageKey = "redo-parcel-item-breakdown-expanded";
  const [expanded, setExpanded] = useState(() => {
    return localStorage.getItem(storageKey) === "true";
  });

  useEffect(() => {
    localStorage.setItem(storageKey, String(expanded));
  }, [expanded, storageKey]);

  const handleIncrement = useHandler((externalId: string) => {
    const current = selectedParcels[parcelIndex]?.itemAllocations ?? [];
    const item = items.find((i) => i.externalId === externalId);
    const inThisParcel = getQuantityInParcel(
      selectedParcels[parcelIndex]!,
      externalId,
    );
    const allocatedElsewhere = getAllocatedQuantity(
      selectedParcels,
      externalId,
      parcelIndex,
    );
    const available = item!.quantity - allocatedElsewhere - inThisParcel;

    // If there's no freely available quantity, auto-steal from another parcel
    // so the user doesn't have to manually zero out Package 1 first.
    if (available <= 0) {
      const sourceIndex = selectedParcels.findIndex(
        (p, i) =>
          i !== parcelIndex &&
          (p.itemAllocations?.find((a) => a.externalId === externalId)
            ?.quantity ?? 0) > 0,
      );
      if (sourceIndex === -1) {
        return;
      }
      const sourceAllocs = selectedParcels[sourceIndex]!.itemAllocations ?? [];
      const decremented = sourceAllocs.map((a) =>
        a.externalId === externalId ? { ...a, quantity: a.quantity - 1 } : a,
      );
      onParcelChange({ itemAllocations: decremented }, sourceIndex);
    }

    const existing = current.find((a) => a.externalId === externalId);
    const incremented = existing
      ? current.map((a) =>
          a.externalId === externalId ? { ...a, quantity: a.quantity + 1 } : a,
        )
      : [...current, { externalId, sku: item?.sku ?? "", quantity: 1 }];
    onParcelChange({ itemAllocations: incremented }, parcelIndex);
  });

  const handleDecrement = useHandler((externalId: string) => {
    const current = selectedParcels[parcelIndex]?.itemAllocations ?? [];
    const updated = current.map((a) =>
      a.externalId === externalId ? { ...a, quantity: a.quantity - 1 } : a,
    );
    onParcelChange({ itemAllocations: updated }, parcelIndex);
  });

  const parcel = selectedParcels[parcelIndex];
  if (!parcel) {
    return null;
  }

  return (
    <Flex dir="column">
      <Flex
        align="center"
        justify="space-between"
        onClick={() => setExpanded(!expanded)}
        pt="md"
        style={{ cursor: "pointer" }}
      >
        <Text fontSize="xs" fontWeight="semibold">
          Item breakdown
        </Text>
        <RedoButton
          IconLeading={expanded ? TriangleDownIcon : TriangleRightIcon}
          size="xs"
        />
      </Flex>
      {expanded && (
        <Flex
          bgColor="primary"
          borderColor="secondary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="none"
          mt="md"
          radius="sm"
        >
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            gap="none"
            p="sm"
          >
            <Flex flex={3}>
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Product
              </Text>
            </Flex>
            <Flex flex={1} justify="center">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Total
              </Text>
            </Flex>
            <Flex flex={1} justify="center">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                Available
              </Text>
            </Flex>
            <Flex flex={2} justify="center">
              <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
                In this package
              </Text>
            </Flex>
          </Flex>
          {items.map((item) => {
            const inThisParcel = getQuantityInParcel(parcel, item.externalId);
            const allocatedElsewhere = getAllocatedQuantity(
              selectedParcels,
              item.externalId,
              parcelIndex,
            );
            const available = item.quantity - allocatedElsewhere - inThisParcel;

            return (
              <Flex
                align="center"
                borderBottomWidth="1px"
                borderColor="secondary"
                borderStyle="solid"
                gap="none"
                key={item.externalId}
                p="sm"
              >
                <Flex dir="column" flex={3}>
                  <Text fontSize="xs" fontWeight="medium">
                    {item.title}
                  </Text>
                  {item.sku && (
                    <Text fontSize="xs" textColor="tertiary">
                      {item.sku}
                    </Text>
                  )}
                </Flex>
                <Flex flex={1} justify="center">
                  <Text fontSize="xs">{item.quantity}</Text>
                </Flex>
                <Flex flex={1} justify="center">
                  <Text
                    fontSize="xs"
                    textColor={available > 0 ? "primary" : "tertiary"}
                  >
                    {available}
                  </Text>
                </Flex>
                <Flex align="center" flex={2} gap="xs" justify="center">
                  <RedoButton
                    disabled={disabled || inThisParcel === 0}
                    hierarchy="tertiary"
                    onClick={() => handleDecrement(item.externalId)}
                    size="xs"
                    text="-"
                  />
                  <Text fontSize="xs" fontWeight="medium">
                    {inThisParcel}
                  </Text>
                  <RedoButton
                    disabled={disabled || inThisParcel >= item.quantity}
                    hierarchy="tertiary"
                    onClick={() => handleIncrement(item.externalId)}
                    size="xs"
                    text="+"
                  />
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
});
